import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Droplets, Zap, ShieldCheck, Wrench } from "lucide-react";
import { AMC, TENURES, waLink } from "@/data/site";
import { Reveal, SectionHead, WaIcon, useLang } from "./common";

export const inr = (n, d = 2) => "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between gap-4"><span className="text-slate-300">{k}</span><span data-testid={testid} className="font-bold">{v}</span></div>
);

const TenureToggle = ({ value, onChange }) => {
  const { t } = useLang();
  return (
    <div className="inline-flex rounded-full bg-[#0F295B]/5 p-1.5 border border-[#0F295B]/10" role="tablist" data-testid="amc-tenure-toggle">
      {TENURES.map((ten) => (
        <button key={ten.id} role="tab" aria-selected={value === ten.id} data-testid={`tenure-${ten.id}`} onClick={() => onChange(ten.id)}
          className={`relative px-4 sm:px-6 py-2.5 rounded-full text-sm font-bold transition-colors ${value === ten.id ? "text-[#0F295B]" : "text-[#0F295B]/60 hover:text-[#0F295B]"}`}>
          {value === ten.id && <motion.span layoutId="tenure-pill" className="absolute inset-0 rounded-full bg-[#F39C12]" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
          <span className="relative font-bold text-base sm:text-lg">{t.amc.tenureLabels[ten.id]}</span>
        </button>
      ))}
    </div>
  );
};

const CapCard = ({ p, i, ten, active, onClick }) => {
  const { t } = useLang();
  return (
    <motion.button whileHover={{ y: -5 }} whileTap={{ scale: 0.98 }} onClick={onClick} data-testid={`amc-capacity-${i}`}
      className={`relative text-left rounded-3xl p-4 sm:p-6 border transition-colors ${active ? "bg-white border-[#F39C12] shadow-[0_0_0_3px_rgba(243,156,18,0.4)]" : "bg-[#F8F9FA] border-[#0F295B]/10 hover:border-[#00A651]/50"}`}>
      {ten === "yearly" && <span className="absolute top-3 right-3 sm:top-4 sm:right-4 text-[10px] font-bold uppercase tracking-wider rounded-full bg-[#00A651] text-white px-2 py-1">{t.amc.badges[i]}</span>}
      <p className={`font-mono2 text-xs tracking-[0.2em] uppercase ${active ? "text-[#00A651]" : "text-[#4A5B78]"}`}>{t.amc.plant}</p>
      <p className={`font-display text-2xl mt-1 ${active ? "text-[#0F295B]" : "text-[#0F295B]"}`}>{p.cap}</p>
      <motion.p key={ten} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`font-display text-2xl sm:text-3xl mt-6 whitespace-nowrap ${active ? "text-[#0F295B]" : "text-[#00A651]"}`}>
        ₹{p[ten].toLocaleString("en-IN")}
        <span className={`text-xs sm:text-sm ml-1 font-body ${active ? "text-[#4A5B78]" : "text-[#4A5B78]"}`}>{t.amc.perMo}</span>
      </motion.p>
    </motion.button>
  );
};

const Summary = ({ plan, ten }) => {
  const { t, lang } = useLang();
  const tenure = TENURES.find((x) => x.id === ten);
  const base = plan[ten] * tenure.months;
  const gst = base * 0.18;
  const waText = lang === "hi"
    ? `नमस्ते Vivan Energia, मुझे अपने ${plan.cap} सोलर प्लांट के लिए ${t.amc.tenureLabels[ten]} AMC प्लान चाहिए (₹${plan[ten]}/माह + GST)।`
    : `Hi Vivan Energia, I want the ${t.amc.tenureLabels[ten]} AMC plan for my ${plan.cap} solar plant (₹${plan[ten]}/month + GST).`;
  return (
    <div data-testid="amc-summary" className="rounded-[2rem] bg-[#0F295B] text-white p-7 sm:p-9 lg:sticky lg:top-28">
      <p className="font-mono2 text-xs tracking-[0.2em] uppercase text-[#F39C12]">{t.amc.yourPlan} · {t.amc.tenureHints[ten]}</p>
      <p className="font-display text-3xl mt-3">{plan.cap} · {t.amc.tenureLabels[ten]}</p>
      <div className="mt-6 space-y-3 text-sm">
        <Row k={`₹${plan[ten]} × ${tenure.months} ${tenure.months > 1 ? t.amc.months[1] : t.amc.months[0]}`} v={inr(base)} testid="amc-base" />
        <Row k={t.amc.gstLine} v={inr(gst)} testid="amc-gst" />
      </div>
      <div className="mt-4 pt-4 border-t-2 border-dashed border-white/20 flex items-end justify-between">
        <span className="font-semibold">{t.amc.netPayable}</span>
        <motion.span key={plan.cap + ten} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="amc-net" className="font-display text-3xl text-[#00A651]">{inr(base + gst)}</motion.span>
      </div>
      <ul className="mt-8 space-y-3">
        {t.amc.inclusions[ten].map((x) => (
          <li key={x} className="flex gap-3 text-sm"><Check className="w-5 h-5 text-[#00A651] shrink-0" />{x}</li>
        ))}
      </ul>
      <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} href={waLink(waText)} target="_blank" rel="noreferrer" data-testid="amc-book-whatsapp"
        className="mt-8 flex items-center justify-center gap-2 rounded-full bg-[#00A651] text-white font-bold py-4 hover:bg-[#008842] transition-colors">
        <WaIcon /> {t.amc.book}
      </motion.a>
    </div>
  );
};

const ICONS = [Droplets, Zap, ShieldCheck, Wrench];

export const AmcPlans = () => {
  const { t } = useLang();
  const [ten, setTen] = useState("yearly");
  const [sel, setSel] = useState(0);
  return (
    <section id="amc" data-testid="amc-section" className="relative bg-white py-24 sm:py-32 overflow-hidden">
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <SectionHead num={t.amc.num} kicker={t.amc.kicker} segs={t.amc.title} desc={t.amc.desc} testid="amc-head" />
        <Reveal className="mb-10"><TenureToggle value={ten} onChange={setTen} /></Reveal>
        <div className="grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-4">
            {AMC.map((p, i) => <CapCard key={p.cap} p={p} i={i} ten={ten} active={sel === i} onClick={() => setSel(i)} />)}
          </div>
          <Reveal className="lg:col-span-5"><Summary plan={AMC[sel]} ten={ten} /></Reveal>
        </div>
        <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.amc.services.map((s, i) => {
            const I = ICONS[i];
            return (
              <Reveal key={s.t} delay={i * 0.08} className="rounded-3xl bg-[#F8F9FA] border border-[#0F295B]/10 p-6 hover:border-[#00A651]/50 transition-colors">
                <div data-testid={`amc-service-${i}`}>
                  <I className="w-7 h-7 text-[#D97D00]" />
                  <p className="font-display text-lg text-[#0F295B] mt-5">{s.t}</p>
                  <p className="text-sm text-[#4A5B78] mt-2 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal className="mt-10 rounded-3xl bg-[#F8F9FA] border border-[#0F295B]/10 p-6 sm:p-8">
          <p className="font-mono2 text-xs tracking-[0.2em] uppercase text-[#00A651] mb-4">{t.amc.termsTitle}</p>
          <ul data-testid="amc-terms" className="grid md:grid-cols-2 gap-x-10 gap-y-3 text-sm text-[#4A5B78]">
            {t.amc.terms.map((x) => <li key={x} className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#00A651] shrink-0" />{x}</li>)}
          </ul>
        </Reveal>
      </div>
    </section>
  );
};
